import { useSortable } from '@dnd-kit/sortable';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { captureError } from '@clatter/platform';

interface ItemWithDnD {
  type: string;
  id: string | number;
  _index?: number;
}

interface UseDnDItemProps<T extends ItemWithDnD> {
  item: T;
  selectedRows: FolderManagerItem[];
  overId?: string | number | undefined | null;
  thumbnailUrl?: string;
  /** default true — false switches to useDraggable (cross-container drag only, no sort computation) */
  sortable?: boolean;
  /** use useDroppable directly instead of through useSortable — eliminates phantom SortableContext registrations */
  droppable?: boolean;
  disabled?: boolean;
}

export const useDnDItem = <T extends ItemWithDnD>({
  item,
  selectedRows,
  overId,
  thumbnailUrl,
  sortable = true,
  droppable = false,
  disabled = false,
}: UseDnDItemProps<T>) => {
  const id = item ? (item._index !== undefined ? item._index : item.id) : '__empty__';
  const data = item ? { type: item?.type, item, thumbnailUrl } : {};
  const isDisabledOrMissing = disabled || !item;

  // All hooks called unconditionally (React hooks rules); only one path is active at a time.
  const sortableResult = useSortable({
    id: String(id),
    data,
    disabled: isDisabledOrMissing || !sortable,
  });

  const draggableResult = useDraggable({
    id: `drag-${String(id)}`,
    data,
    disabled: isDisabledOrMissing || sortable,
  });

  // Use a non-conflicting id when droppable=false so the disabled useDroppable
  // doesn't dispatch UnregisterDroppable for the same id that useSortable registered.
  const droppableResult = useDroppable({
    id: droppable ? String(id) : `__noop_droppable_${String(id)}`,
    data,
    disabled: isDisabledOrMissing || !droppable,
  });

  if (!item) {
    captureError(new Error('Item not provided'), { component: 'useDnDItem', skipConsole: true });
    return;
  }

  const isSelected = (selectedRows || []).some((row: any) => {
    if ((row as any).instanceId && (item as any).instanceId) {
      return row.instanceId === (item as any).instanceId;
    }
    return row._index !== undefined ? row._index === item._index : row.id === item.id && row.type === item.type;
  });

  const isOver = droppable
    ? droppableResult.isOver
    : sortable
    ? overId !== undefined && overId !== null && String(overId) === String(id)
    : false;

  if (disabled) {
    return {
      isOver: false,
      isSelected,
      listeners: {},
      attributes: {},
      setNodeRef: null,
      itemStyle: {},
      isDragging: false,
      isDisabled: true,
    };
  }

  const { attributes, listeners, setNodeRef: dragSetRef, isDragging } = sortable ? sortableResult : draggableResult;

  const setNodeRef = droppable
    ? (el: HTMLElement | null) => { droppableResult.setNodeRef(el); dragSetRef(el); }
    : dragSetRef;

  return {
    isOver,
    isSelected,
    attributes,
    listeners,
    setNodeRef,
    isDragging,
    isDisabled: false,
    // suppress transform to prevent reorder animation
    itemStyle: { transform: undefined, transition: undefined },
  };
};
